import { isNativeAppShell } from './mobileView';
import { ensurePortraitOrientation } from '../../../nexa_mobile/src/capacitorPlatform';

function isUserCancelled(error) {
  const message = String(error?.message || error || '').toLowerCase();
  return (
    message.includes('cancel')
    || message.includes('cancelled')
    || message.includes('canceled')
    || error?.code === 'USER_CANCELLED'
  );
}

function isCameraUnavailable(error) {
  const message = String(error?.message || error || '').toLowerCase();
  return (
    message.includes('unavailable')
    || message.includes('no camera')
    || message.includes('camera not')
    || message.includes('not available')
    || message.includes('simulator')
    || message.includes('could not be opened')
    || message.includes('unable to')
    || message.includes('failed to start')
  );
}

async function takePhoto(Camera, CameraResultType, CameraSource, source) {
  return Camera.getPhoto({
    quality: 85,
    allowEditing: false,
    correctOrientation: true,
    resultType: CameraResultType.DataUrl,
    source,
    saveToGallery: false,
  });
}

/**
 * Capture via Capacitor Camera plugin (native iOS / Android only).
 * Tries the device camera first; if the camera is unavailable (common on
 * simulators/emulators), falls back to the photo library.
 */
export async function captureNativeCameraDataUrl() {
  if (!isNativeAppShell()) {
    return null;
  }

  try {
    const { Camera, CameraResultType, CameraSource } = await import('@capacitor/camera');

    try {
      const permissions = await Camera.requestPermissions({ permissions: ['camera', 'photos'] });
      if (permissions?.camera === 'denied' && permissions?.photos === 'denied') {
        throw new Error('Camera permission denied');
      }
    } catch (permissionError) {
      // Older plugin builds may not support requestPermissions options — continue to getPhoto.
      if (String(permissionError?.message || '').toLowerCase().includes('denied')) {
        throw permissionError;
      }
    }

    try {
      const photo = await takePhoto(Camera, CameraResultType, CameraSource, CameraSource.Camera);
      return photo?.dataUrl || null;
    } catch (cameraError) {
      if (isUserCancelled(cameraError)) {
        throw cameraError;
      }
      // Simulators / some emulators have no camera hardware — photo library still works.
      try {
        const photo = await takePhoto(Camera, CameraResultType, CameraSource, CameraSource.Photos);
        return photo?.dataUrl || null;
      } catch (photosError) {
        if (isUserCancelled(photosError)) {
          throw photosError;
        }
        throw cameraError;
      }
    }
  } finally {
    await ensurePortraitOrientation();
  }
}

export function getNativeCameraErrorMessage(error) {
  const message = String(error?.message || error || '').toLowerCase();
  if (isUserCancelled(error)) {
    return '';
  }
  if (message.includes('denied') || message.includes('permission')) {
    return 'Camera permission denied. Enable camera permission for RoomNexa in phone settings.';
  }
  if (isCameraUnavailable(error)) {
    return (
      'Camera is not available on this device/simulator. '
      + 'Use a physical phone, or turn off Camera upload to choose a file.'
    );
  }
  return 'Camera could not be opened. Please try again or turn off Camera upload to choose a file.';
}
